import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

interface HydrationProgressProps {
  completedCount: number;
  failedCount: number;
  totalCount: number;
  currentlyHydrating: string | null;
}

export const HydrationProgress: React.FC<HydrationProgressProps> = ({
  completedCount,
  failedCount,
  totalCount,
  currentlyHydrating
}) => {
  const { darkMode } = useTheme();
  
  // Calculate percentages for the progress bar
  const completedPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const failedPercentage = totalCount > 0 ? (failedCount / totalCount) * 100 : 0;
  const currentPercentage = currentlyHydrating && totalCount > 0 ? (1 / totalCount) * 100 : 0;
  
  // Calculate pending count correctly
  const pendingCount = Math.max(0, totalCount - completedCount - failedCount);

  return (
    <div className={`p-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      {/* Progress bar */}
      <div className="flex justify-between items-center">
        <div>
          <span className="text-sm font-medium">
            Progress: {completedCount} of {totalCount} files
          </span>
          {failedCount > 0 && (
            <span className="ml-2 text-sm text-red-500">
              ({failedCount} failed)
            </span>
          )}
        </div>
        <div className="h-2 w-40 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full flex">
            {/* Completed portion */}
            <div 
              className="h-full bg-green-500" 
              style={{ width: `${completedPercentage}%` }}
            ></div>
            
            {/* Failed portion */}
            {failedCount > 0 && (
              <div 
                className="h-full bg-red-500" 
                style={{ width: `${failedPercentage}%` }}
              ></div>
            )}
            
            {/* Currently hydrating portion */}
            {currentlyHydrating && (
              <div 
                className="h-full bg-blue-500 animate-pulse" 
                style={{ width: `${currentPercentage}%` }}
              ></div>
            )}
          </div>
        </div>
      </div>
      
      {/* Detailed status counters */}
      <div className="mt-2 flex justify-center space-x-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
          <span>Completed: {completedCount}</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
          <span>Failed: {failedCount}</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-gray-500 mr-1"></div>
          <span>Pending: {pendingCount}</span>
        </div>
      </div>
      
      {/* Hydration order explanation */}
      <div className="mt-3 text-xs text-center">
        <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <span className="font-medium">Hydration Order:</span> Files are hydrated in dependency order (dependencies first)
        </div>
      </div>
    </div>
  );
};
